/**
 * VideoPlayer: Reproductor de video personalizado para Test C
 * 
 * Características:
 * - Reproducción de fragmentos de video de 20 segundos desde el servidor
 * - Máximo 2 reproducciones por fragmento
 * - Presentación anónima (sin título, artista o información visible)
 * - Control automático de duración
 * - Reproduce video completo con audio e imagen
 * 
 * @author Ana Lizcano
 * @version 1.0.0
 */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { IconButton, LinearProgress } from '@mui/material';
import { PlayArrow, Pause, MusicNote } from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

const VideoContainer = styled(MDBox)(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
  justifyContent: 'center',
}));

const ControlsContainer = styled(MDBox)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
  maxWidth: '400px',
});

const PlayButton = styled(IconButton)(({ theme, disabled }) => ({
  backgroundColor: disabled ? '#e9ecef' : '#2196f3',
  color: disabled ? '#6c757d' : '#ffffff',
  width: '64px',
  height: '64px',
  boxShadow: disabled ? 'none' : '0 4px 12px rgba(33, 150, 243, 0.3)',
  '&:hover': {
    backgroundColor: disabled ? '#e9ecef' : '#1976d2',
    boxShadow: disabled ? 'none' : '0 6px 16px rgba(33, 150, 243, 0.4)',
  }
}));

const ProgressContainer = styled(MDBox)({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const TimeDisplay = styled(MDBox)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});

const VisibleVideo = styled('video')({
  width: '100%',
  maxWidth: '600px',
  borderRadius: '8px',
  backgroundColor: '#000',
  pointerEvents: 'none',
  userSelect: 'none',
  '-webkit-user-select': 'none',
  '-moz-user-select': 'none',
  '-ms-user-select': 'none',
});

const VideoPlayer = ({ 
  src, 
  fragmentDuration = 20, 
  maxPlays = 2, 
  onPlayCountChange,
  autoPlay = false 
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(fragmentDuration);
  const [playCount, setPlayCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const canPlay = playCount < maxPlays;

  // Reset play count when src changes
  useEffect(() => {
    setPlayCount(0);
    setCurrentTime(0);
    setIsPlaying(false);
    setError(null);
    
    // Validate src URL
    if (!src) {
      setError('No se proporcionó URL de video');
      setIsLoading(false);
      return;
    }
    
    
    // Protección adicional contra compartir
    const preventContextMenu = (e) => e.preventDefault();
    const preventKeyboardShortcuts = (e) => {
      // Bloquear F12, Ctrl+U, Ctrl+Shift+I, Ctrl+S, etc.
      if (e.key === 'F12' || 
          (e.ctrlKey && ['u', 'U', 's', 'S'].includes(e.key)) ||
          (e.ctrlKey && e.shiftKey && ['i', 'I', 'j', 'J'].includes(e.key))) {
        e.preventDefault();
        return false;
      }
    };
    
    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('keydown', preventKeyboardShortcuts);
    
    return () => {
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('keydown', preventKeyboardShortcuts);
    };
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedData = () => {
      setIsLoading(false);
      setError(null);
      setDuration(fragmentDuration);
      
      // Ocultar metadatos del video
      video.removeAttribute('title');
      video.removeAttribute('alt');
      
      if (autoPlay && canPlay) {
        handlePlay();
      }
    };

    const handleTimeUpdate = () => {
      const current = video.currentTime;
      setCurrentTime(current);
      
      // Detener automáticamente después de 20 segundos
      if (current >= fragmentDuration) {
        handleVideoEnd();
      }
    };

    const handleEnded = () => {
      handleVideoEnd();
    };

    const handleError = () => {
      setError('Error al cargar el fragmento de video');
      setIsLoading(false);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('loadstart', handleLoadStart);

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('loadstart', handleLoadStart);
    };
  }, [fragmentDuration, autoPlay, canPlay]);

  const handleVideoEnd = () => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handlePlay = async () => {
    if (!canPlay || !videoRef.current) return;

    try {
      videoRef.current.currentTime = 0;
      await videoRef.current.play();
      setIsPlaying(true);
      
      // Incrementar contador cuando se inicia la reproducción
      setPlayCount(prev => {
        const newCount = prev + 1;
        if (onPlayCountChange) {
          onPlayCountChange(newCount);
        }
        return newCount;
      });
    } catch (err) {
      setError('Error al reproducir el fragmento de video');
    }
  };

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleTogglePlay = () => {
    if (isPlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (error) {
    return (
      <VideoContainer>
        <MusicNote sx={{ fontSize: 48, color: '#dc3545', mb: 2 }} />
        <MDTypography variant="h6" color="error" textAlign="center" mb={2}>
          Error al cargar el fragmento musical
        </MDTypography>
        <MDTypography variant="body2" color="text" textAlign="center" mb={2}>
          {error}
        </MDTypography>
        <MDBox sx={{ 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7', 
          borderRadius: '8px', 
          padding: '12px', 
          width: '100%',
          maxWidth: '400px'
        }}>
          <MDTypography variant="caption" color="warning" textAlign="center" sx={{ display: 'block', mb: 1 }}>
            <strong>Posibles causas:</strong>
          </MDTypography>
          <MDTypography variant="caption" color="text" textAlign="left" sx={{ fontSize: '0.75rem' }}>
            • El archivo de video no existe en la ruta especificada<br/>
            • Problema de conectividad de red<br/>
            • Formato de video no compatible<br/><br/>
            <strong>Ruta esperada:</strong> {src}
          </MDTypography>
        </MDBox>
      </VideoContainer>
    );
  }

  return (
    <VideoContainer>
      {/* Video visible con controles básicos */}
      <VisibleVideo
        ref={videoRef}
        src={src}
        preload="auto"
        muted={false}
        playsInline
        controls={false}
        controlsList="nodownload noplaybackrate nofullscreen"
        disablePictureInPicture
        disableRemotePlayback
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        onSelectStart={(e) => e.preventDefault()}
        title=""
        alt=""
      />
      
      {isLoading ? (
        <>
          <MusicNote sx={{ fontSize: 48, color: '#6c757d', mb: 2 }} />
          <MDTypography variant="h6" color="text" textAlign="center">
            Cargando fragmento musical...
          </MDTypography>
          <LinearProgress sx={{ width: '100%', mt: 2 }} />
        </>
      ) : (
        <>
          <ControlsContainer>
            <PlayButton
              onClick={handleTogglePlay}
              disabled={!canPlay}
              title={canPlay ? (isPlaying ? 'Pausar' : 'Reproducir') : 'Sin reproducciones restantes'}
            >
              {isPlaying ? <Pause sx={{ fontSize: 32 }} /> : <PlayArrow sx={{ fontSize: 32 }} />}
            </PlayButton>

            <ProgressContainer sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#e9ecef',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#2196f3',
                    borderRadius: 4,
                  }
                }}
              />
              <TimeDisplay>
                <MDTypography variant="caption" color="text">
                  {formatTime(currentTime)}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {formatTime(duration)}
                </MDTypography>
              </TimeDisplay>
            </ProgressContainer>
          </ControlsContainer>

        </>
      )}
    </VideoContainer>
  );
};

VideoPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  fragmentDuration: PropTypes.number,
  maxPlays: PropTypes.number,
  onPlayCountChange: PropTypes.func,
  autoPlay: PropTypes.bool
};

export default VideoPlayer;